(() => {
  if (window.__avriqoColourGallerySyncStarted) return;
  window.__avriqoColourGallerySyncStarted = true;

  const allowedHandles = new Set([
    'blank-solid-polo-regular-fit',
    'blank-solid-t-shirt-regular-fit',
    'blank-solid-t-shirt-oversized-fit',
    'drawstring-trouser-straight-leg',
  ]);
  const instances = new WeakMap();
  const pendingColours = new Map();

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/grey/g, 'gray')
      .replace(/\s+/g, ' ')
      .trim();

  const getColourInputs = (picker) =>
    Array.from(picker.querySelectorAll('input[type="radio"][data-option-name="Color"]'));

  const getSlides = (gallery) => Array.from(gallery.querySelectorAll('slideshow-slide'));

  const getSlideColour = (slide) => normalize(slide?.querySelector('img')?.alt);

  const findSlideIndex = (gallery, colour) => {
    const target = normalize(colour);
    return getSlides(gallery).findIndex((slide) => getSlideColour(slide).includes(target));
  };

  const findColourInput = (picker, slide) => {
    const alt = getSlideColour(slide);
    if (!alt) return null;

    return (
      getColourInputs(picker)
        .sort((a, b) => b.value.length - a.value.length)
        .find((input) => alt.includes(normalize(input.value))) || null
    );
  };

  const primeImage = (image, priority = 'low') => {
    if (!(image instanceof HTMLImageElement)) return;
    image.loading = 'eager';
    image.fetchPriority = priority;
    image.decode?.().catch(() => {});
  };

  const primeNearbyImages = (gallery, colour) => {
    const slides = getSlides(gallery);
    const index = findSlideIndex(gallery, colour);
    if (index < 0 || !slides.length) return;

    primeImage(slides[index]?.querySelector('img'), 'high');
    primeImage(slides[(index + 1) % slides.length]?.querySelector('img'));
    primeImage(slides[(index - 1 + slides.length) % slides.length]?.querySelector('img'));
  };

  const selectSlideForColour = (gallery, colour, animate = false) => {
    const slides = getSlides(gallery);
    const index = findSlideIndex(gallery, colour);
    if (index < 0) return false;

    primeNearbyImages(gallery, colour);

    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    const slideId = slides[index]?.getAttribute('slide-id');
    if (typeof slideshow?.select === 'function') {
      slideshow.select(slideId ? { id: slideId } : index, undefined, { animate });
      return true;
    }

    const dot = gallery.querySelector(
      `button[aria-label="Slide ${index + 1} of ${slides.length}"]`
    );
    if (dot && dot.getAttribute('aria-selected') !== 'true') dot.click();
    return Boolean(dot);
  };

  const setup = (root) => {
    const picker = root.querySelector('variant-picker[data-product-handle]');
    const gallery = root.querySelector('media-gallery');
    const handle = picker?.dataset.productHandle;

    if (!picker || !gallery || !allowedHandles.has(handle)) return;

    const current = instances.get(root);
    if (current?.picker === picker && current?.gallery === gallery) return;

    if (current) {
      current.picker.removeEventListener('change', current.onChange, true);
      current.gallery.removeEventListener('click', current.onNavigation, true);
      current.gallery.removeEventListener('touchstart', current.onTouchStart, true);
      current.gallery.removeEventListener('touchmove', current.onTouchMove, true);
      current.gallery.removeEventListener('touchend', current.onTouchEnd, true);
      current.gallery.removeEventListener('touchcancel', current.onTouchCancel, true);
    }

    const checkedColour = getColourInputs(picker).find((input) => input.checked)?.value;
    const state = {
      gallery,
      picker,
      handle,
      previewColour: pendingColours.get(handle) || checkedColour || null,
      galleryDrivenChange: false,
      touchStartX: null,
      touchStartY: null,
      swiping: false,
      onChange: null,
      onNavigation: null,
      onTouchStart: null,
      onTouchMove: null,
      onTouchEnd: null,
      onTouchCancel: null,
    };

    const getSelectedDotIndex = () => {
      const selectedDot = gallery.querySelector(
        'button[aria-label^="Slide "][aria-selected="true"]'
      );
      return Number(selectedDot?.getAttribute('aria-label')?.match(/^Slide (\d+) of/)?.[1]);
    };

    const advanceColour = (offset) => {
      const colourInputs = getColourInputs(picker);
      const baseColour = state.previewColour || colourInputs.find((input) => input.checked)?.value;
      const checkedIndex = colourInputs.findIndex(
        (input) => normalize(input.value) === normalize(baseColour)
      );
      if (!offset || checkedIndex < 0 || colourInputs.length < 2) return;

      const direction = offset < 0 ? -1 : 1;
      let targetIndex =
        (checkedIndex + offset + colourInputs.length) % colourInputs.length;
      let targetInput = colourInputs[targetIndex];

      if (targetInput?.disabled || targetInput?.getAttribute('aria-disabled') === 'true') {
        targetInput = null;
        for (let step = 1; step < colourInputs.length; step += 1) {
          targetIndex =
            (checkedIndex + direction * step + colourInputs.length) % colourInputs.length;
          const candidate = colourInputs[targetIndex];
          if (!candidate.disabled && candidate.getAttribute('aria-disabled') !== 'true') {
            targetInput = candidate;
            break;
          }
        }
      }

      if (!targetInput || normalize(targetInput.value) === normalize(baseColour)) return;

      state.previewColour = targetInput.value;
      pendingColours.set(handle, targetInput.value);
      state.galleryDrivenChange = true;
      selectSlideForColour(gallery, targetInput.value, true);
      targetInput.click();
      queueMicrotask(() => {
        state.galleryDrivenChange = false;
      });
    };

    state.onNavigation = (event) => {
      if (!event.isTrusted) return;
      const button = event.target.closest?.('button[aria-label]');
      if (!button || !gallery.contains(button)) return;

      const label = button.getAttribute('aria-label') || '';
      let offset = 0;

      if (label === 'Next slide') offset = 1;
      if (label === 'Previous slide') offset = -1;

      const clickedIndex = Number(label.match(/^Slide (\d+) of/)?.[1]);
      if (clickedIndex) {
        const currentIndex = getSelectedDotIndex();
        const slideCount = getSlides(gallery).length;
        if (!currentIndex || !slideCount) return;
        offset = clickedIndex - currentIndex;
        if (Math.abs(offset) > slideCount / 2) {
          offset += offset > 0 ? -slideCount : slideCount;
        }
      }

      if (!offset) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      advanceColour(offset);
    };

    state.onTouchStart = (event) => {
      state.touchStartX = event.touches?.[0]?.clientX ?? null;
      state.touchStartY = event.touches?.[0]?.clientY ?? null;
      state.swiping = false;
    };

    state.onTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch || state.touchStartX == null || state.touchStartY == null) return;
      const deltaX = touch.clientX - state.touchStartX;
      const deltaY = touch.clientY - state.touchStartY;
      if (Math.abs(deltaX) > 18 && Math.abs(deltaX) > Math.abs(deltaY)) {
        state.swiping = true;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    state.onTouchEnd = (event) => {
      const touch = event.changedTouches?.[0];
      const deltaX =
        touch && state.touchStartX != null ? touch.clientX - state.touchStartX : 0;

      if (state.swiping && Math.abs(deltaX) > 24) {
        event.preventDefault();
        event.stopImmediatePropagation();
        advanceColour(deltaX < 0 ? 1 : -1);
      }

      state.touchStartX = null;
      state.touchStartY = null;
      state.swiping = false;
    };

    state.onTouchCancel = () => {
      state.touchStartX = null;
      state.touchStartY = null;
      state.swiping = false;
    };

    state.onChange = (event) => {
      const input = event.target.closest?.(
        'input[type="radio"][data-option-name="Color"]'
      );
      if (!input) return;

      state.previewColour = input.value;
      pendingColours.set(handle, input.value);

      if (!state.galleryDrivenChange) {
        selectSlideForColour(gallery, input.value, false);
        queueMicrotask(() => selectSlideForColour(gallery, input.value, false));
      } else {
        primeNearbyImages(gallery, input.value);
      }
    };

    picker.addEventListener('change', state.onChange, true);
    gallery.addEventListener('click', state.onNavigation, true);
    gallery.addEventListener('touchstart', state.onTouchStart, {
      capture: true,
      passive: true,
    });
    gallery.addEventListener('touchmove', state.onTouchMove, {
      capture: true,
      passive: false,
    });
    gallery.addEventListener('touchend', state.onTouchEnd, {
      capture: true,
      passive: false,
    });
    gallery.addEventListener('touchcancel', state.onTouchCancel, true);
    instances.set(root, state);

    if (state.previewColour) {
      selectSlideForColour(gallery, state.previewColour, false);
      requestAnimationFrame(() => {
        selectSlideForColour(gallery, state.previewColour, false);
        const selected = getColourInputs(picker).find((input) => input.checked)?.value;
        if (normalize(selected) === normalize(state.previewColour)) {
          pendingColours.delete(handle);
        }
      });
    }
  };

  let scanQueued = false;
  const scan = () => {
    scanQueued = false;
    document.querySelectorAll('.product-information').forEach(setup);
  };
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(scan);
  };

  queueScan();
  document.addEventListener('shopify:section:load', queueScan);
  new MutationObserver(queueScan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
